/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import Media from 'react-media';

import { Divider, breakpoints, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import useUserAgent from 'bundles/page/hooks/useUserAgent';
import { MAX_NUMBER_OF_KIM_ITEMS } from 'bundles/unified-description-page-common/constants';
import { useStreamlinedXDP } from 'bundles/unified-description-page-common/utils/useStreamlinedXDP';

const styles = {
  wrapper: (theme: Theme) => css`
    ${theme.breakpoints.up('md')} {
      border: 1px solid ${theme.palette.gray[300]};
      border-radius: ${theme.spacing(8)};
      margin: -82px auto 0;
      background-color: var(--cds-color-white-0);
      box-shadow: ${theme.spacing(0, 8, 16, 0)} rgb(0 0 0 / 16%);
      align-items: center;
      display: flex;
      justify-content: space-evenly;
      z-index: 1;
      position: relative;
    }

    ${theme.breakpoints.down('sm')} {
      padding-top: ${theme.spacing(32)};
    }
  `,
  items: (theme: Theme) => css`
    ${theme.breakpoints.up('md')} {
      display: flex;
      flex: 1 1 0px;
      align-items: center;
      padding: ${theme.spacing(48, 0)};
      justify-content: center;
    }

    ${theme.breakpoints.down('xs')} {
      padding: ${theme.spacing(0)};
    }

    .cds-ContextualHelp-label p {
      color: ${theme.palette.gray[700]};
    }
  `,
  desktopItem: (theme: Theme, width?: string) => css`
    max-width: 35%;
    width: ${width ?? 'auto'};
    padding: ${theme.spacing(0, 48)};
    align-self: stretch;
    display: flex;

    & + & {
      border-left: 1px solid ${theme.palette.gray[300]};
    }

    .cds-ContextualHelp-container {
      /* keeps subtitle CTAs on the same line for desktop */
      display: flex !important;
    }
  `,
  itemWrapper: css`
    align-self: center;
    margin: 0 auto;
  `,
  mobileItem: (theme: Theme) => css`
    margin: ${theme.spacing(16, 0)};
  `,
  mobileHorizontalDivider: (theme: Theme) => css`
    width: 100%;
    left: 0;
    position: absolute;
    margin: ${theme.spacing(24, 0)};
    height: 2px;
  `,
  mobileItemStreamlinedXDP: css`
    display: flex;
    align-items: center;

    & + & {
      margin-top: var(--cds-spacing-150);
    }
  `,
  wrapperStreamlinedXDP: css`
    padding-top: var(--cds-spacing-400);
  `,
};

type Props = {
  keyInformationItems: React.ReactNode[];
};

const KeyInformationHorizontalModule = ({ keyInformationItems }: Props) => {
  const userAgent = useUserAgent();
  const theme = useTheme();
  const isStreamlinedXDP = useStreamlinedXDP();

  const keyInfoItems = keyInformationItems.filter((item) => {
    return item !== false;
  });

  const renderDesktopInfoItems = () => {
    const itemWidth =
      keyInformationItems.length < MAX_NUMBER_OF_KIM_ITEMS ? `${Math.floor(100 / keyInfoItems.length)}%` : undefined;
    return keyInfoItems.map((keyInfoItem, index) => (
      <div key={`key-info-${index}`} css={styles.desktopItem(theme, itemWidth)}>
        <div css={styles.itemWrapper}>{keyInfoItem}</div>
      </div>
    ));
  };

  const renderMobileInfoItems = () => {
    return keyInfoItems.map((keyInfoItem, index) => (
      <div key={`key-info-${index}`} css={isStreamlinedXDP ? styles.mobileItemStreamlinedXDP : styles.mobileItem}>
        {keyInfoItem}
        {index === keyInfoItems.length - 1 && !isStreamlinedXDP && (
          <Divider orientation="horizontal" css={styles.mobileHorizontalDivider} />
        )}
      </div>
    ));
  };

  return (
    <Media query={{ maxWidth: breakpoints.values.md - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
      {(matches) => (
        <section css={matches && isStreamlinedXDP ? styles.wrapperStreamlinedXDP : styles.wrapper}>
          <div css={styles.items}>{matches ? renderMobileInfoItems() : renderDesktopInfoItems()}</div>
        </section>
      )}
    </Media>
  );
};

export default KeyInformationHorizontalModule;
